import { AlertCircle, AlertTriangle } from 'lucide-react';
import type { Issue } from '@branchworks/shared';

/**
 * Inline issues banner at the top of the detail panel, between the header
 * and the type selector. Surfaces validation issues for the currently
 * selected node *in context* as authors edit — same goal as the prototype's
 * version.
 *
 * Errors are listed before warnings. Each issue shows its message and a
 * short sub-location ("Choice 2 · visibility"); the issue code chip is
 * deferred to the Problems modal in Phase 1b.3.
 */
export function NodeIssuesBanner({ issues }: { issues: Issue[] }) {
  if (issues.length === 0) return null;
  const errors = issues.filter((i) => i.level === 'error');
  const warnings = issues.filter((i) => i.level === 'warning');
  const hasErrors = errors.length > 0;

  const trim = hasErrors ? '#A14A38' : '#B8551F';
  const wash = hasErrors ? '#FBF1EE' : '#FBF4ED';

  return (
    <div
      className="rounded-md border p-3"
      style={{ borderColor: trim, background: wash }}
    >
      <div
        className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest mb-2"
        style={{ color: trim }}
      >
        {hasErrors ? <AlertCircle size={13} /> : <AlertTriangle size={13} />}
        {errors.length > 0 && <span>{errors.length} error{errors.length === 1 ? '' : 's'}</span>}
        {errors.length > 0 && warnings.length > 0 && <span>·</span>}
        {warnings.length > 0 && <span>{warnings.length} warning{warnings.length === 1 ? '' : 's'}</span>}
      </div>
      <ul className="space-y-1.5">
        {[...errors, ...warnings].map((issue, i) => (
          <li key={i} className="text-xs flex items-start gap-2 text-ink">
            <span
              className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full"
              style={{ background: issue.level === 'error' ? '#A14A38' : '#B8551F' }}
            />
            <div className="min-w-0">
              <div>{issue.message}</div>
              {sublocation(issue) && (
                <div className="text-[10px] uppercase tracking-widest text-ink-2 mt-0.5">
                  {sublocation(issue)}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Render a short human-readable sub-location for an issue: where within the
 * selected node the problem lives. "Choice 2 · visibility" reads better than
 * dumping `path.kind/path.choiceIndex/path.sub` directly.
 */
function sublocation(issue: Issue): string | null {
  const p = issue.path;
  const parts: string[] = [];
  if (p.choiceIndex !== undefined) {
    parts.push(`Choice ${p.choiceIndex + 1}${p.choiceLabel ? ` · ${truncate(p.choiceLabel, 24)}` : ''}`);
  }
  if (p.variantIndex !== undefined) parts.push(`Variant ${p.variantIndex + 1}`);
  if (p.destinationIndex !== undefined) parts.push(`Destination ${p.destinationIndex + 1}`);
  if (p.feedbackIndex !== undefined) parts.push(`Feedback ${p.feedbackIndex + 1}`);
  if (p.sub) parts.push(p.sub);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
